import { request, streamRequest } from '@h3/request';
import { H3 } from '@h3/types';

/** 向ai发送指令 */
export function generateText(message: string, user: string) {
    return request<
        H3.Http.Response.H3Response<{
            choices: Array<{ message: { content: string } }>;
            id: string;
            usage: { promptTokens: number };
        }>
    >('/v1/openai/chat/completions', {
        method: 'post',
        data: {
            message,
            user,
        },
    });
}

/**
 *
 * @param id 消息id
 * @param usefulFlag 是否有用标识
 */
export function comment(id: string, usefulFlag: boolean) {
    return request<H3.Http.Response.H3Response>('v1/openai/choice/useful', {
        method: 'post',
        data: {
            id,
            usefulFlag,
        },
    });
}

/**
 * 流式AI对话
 * @param engineCode 引擎编码
 * @param userId 用户id
 * @param sceneId 场景id（用于识别一个场景下所有的会话）
 * @param message prompt
 */
export function chatStream(engineCode: string, userId: string, sceneId: string, message: string, init?: RequestInit) {
    return streamRequest('/v1/ai-service/stream-chat', {
        method: H3.Http.MethodType.POST,
        body: JSON.stringify({
            engineCode,
            userId,
            sceneId,
            message,
        }),
        ...init,
    });
}

/**
 * 获取指定表单AI智能识别配置
 * @param schemaCode 表单编码
 */
export function loadAIAssignSetting(schemaCode: string) {
    return request<H3.Http.Response.H3Response<H3.AI.IAIAssignSetting>>(
        `/v1/toolkit/ai/extraction/setting/${schemaCode}`,
        {
            method: H3.Http.MethodType.GET,
        },
    );
}

/**
 * 获取Ai智能填单识别结果
 * @param schemaCode 表单编码
 * @param prompt 识别内容
 */
export function aiExtraction(schemaCode: string, prompt: H3.AI.IAIAssignPrompt[]) {
    return request<H3.Http.Response.H3Response<Record<string, unknown>>>('/v1/ai-service/agent/form/extraction', {
        method: H3.Http.MethodType.POST,
        data: {
            schemaCode,
            messages: prompt,
        },
        disableFrontTimeoutError: true,
    });
}

export function getSpeechRecognitionConfig() {
    return request<H3.Http.Response.H3Response<{ appKey: string; accessToken: string }>>(
        '/v1/ai-service/agent/voice/auth',
        {
            method: H3.Http.MethodType.GET,
        },
    );
}

/** 获取ai识别模板信息(用于表单设计器识别字段) */
export function getAIRecognitionTemplate(params: { templateCode: string }) {
    return request<H3.Http.Response.H3Response<H3.AI.IAIRecognitionTemplateModel>>(
        `/v1/toolkit/ai/recognition/template/${params.templateCode}`,
        {
            method: H3.Http.MethodType.GET,
        },
    );
}

/** 获取ai识别模板列表(用于表单设计器) */
export function getAIRecognitionTemplateList(params: { templateType: H3.AI.AIRecognitionTemplateType }) {
    return request<H3.Http.Response.H3Response<H3.AI.Response.IAIRecognitionTemplateDetail[]>>(
        '/v1/toolkit/ai/recognition/template/list',
        {
            method: H3.Http.MethodType.GET,
            params,
        },
    );
}

/** 获取ai识别模板信息 */
export function getAIRecognitionTemplateDetail(params: { templateCode: string }) {
    return request<H3.Http.Response.H3Response<H3.AI.IAIRecognitionTemplateModel>>(
        `/v1/toolkit/ai/recognition/template/${params.templateCode}/detail`,
        {
            method: H3.Http.MethodType.GET,
        },
    );
}

/** 删除ai识别模板 */
export function deleteAIRecognitionTemplate(params: { templateCode: string }) {
    return request<H3.Http.Response.H3Response<boolean>>(`/v1/toolkit/ai/recognition/template/${params.templateCode}`, {
        method: H3.Http.MethodType.DELETE,
    });
}

/** 保存ai识别模板信息 */
export function saveAIRecognitionTemplate(params: H3.AI.Request.IAIRecognitionTemplate) {
    return request<H3.Http.Response.H3Response<H3.AI.IAIRecognitionTemplateModel>>(
        '/v1/toolkit/ai/recognition/template',
        {
            method: H3.Http.MethodType.POST,
            data: params,
        },
    );
}

/** 获取ai识别模板列表 */
export function getAIRecognitionTemplateDefinitions(params: { templateType: H3.AI.AIRecognitionTemplateType }) {
    return request<H3.Http.Response.H3Response<H3.AI.Response.IAIRecognitionTemplateDefinitions[]>>(
        '/v1/toolkit/ai/recognition/template/definitions',
        {
            method: H3.Http.MethodType.GET,
            params,
        },
    );
}

/** 智能配置识别模板信息 */
export function getAIRecognitionFieldExtraction(params: { fileId: string }, init?: RequestInit) {
    return request<H3.Http.Response.H3Response<H3.AI.ITableTemplateModel[]>>(
        '/v1/ai-service/agent/ocr/field-extraction',
        {
            method: H3.Http.MethodType.GET,
            params,
            ...init,
        },
    );
}

/** 自定义模板识别接口 */
export function invokeAIRecognitionOcrExtraction(
    params: {
        schemaCode: string;
        templateCode: string;
        fileId: string;
        fields: string[];
    },
    options?: H3.Http.Request.RequestOptions,
) {
    return request<H3.Http.Response.H3Response<H3.AI.Response.IAIRecognitionOcrExtraction[]>>(
        '/v1/ai-service/agent/ocr/extraction',
        {
            method: H3.Http.MethodType.POST,
            data: params,
            ...options,
        },
    );
}

/** 获取用量日志 */
export function getFeatureUsageLog(params: {
    offset: number;
    limit: number;
    featureCode?: H3.AI.AIFeatureType;
    startTime?: string;
    endTime?: string;
}) {
    return request<
        H3.Http.Response.H3Response<{
            logList: H3.AI.Response.IFeatureUsageLogModel[];
            totalCount: number;
        }>
    >('/v1/toolkit/ai/feature/getUsageLog', {
        method: H3.Http.MethodType.POST,
        data: params,
    });
}

/** 获取用量日志(月用量) */
export function getFeatureUsageLogByMonthly(params: { offset: number; limit: number }) {
    return request<
        H3.Http.Response.H3Response<{
            logList: H3.AI.Response.IMonthlyFeatureUsageLogModel[];
            totalCount: number;
        }>
    >('/v1/toolkit/ai/feature/getMonthlyUsageLog', {
        method: H3.Http.MethodType.POST,
        data: params,
    });
}

/** 获取用量获取列表 */
export function getFeatureRecordList() {
    return request<H3.Http.Response.H3Response<H3.AI.Response.IFeatureRecordModel[]>>(
        '/v1/toolkit/ai/feature/getOcrOperationRecordList',
        {
            method: H3.Http.MethodType.POST,
        },
    );
}

/** 获取启用清单 */
export function getAIEnableList(params: {
    offset: number;
    limit: number;
    featureType: H3.AI.AIFeatureType;
    appCode?: string;
}) {
    return request<
        H3.Http.Response.H3Response<{
            enableList: H3.AI.Response.IAIEnableModel[];
            totalCount: number;
        }>
    >('/v1/toolkit/ai/feature/getEnableList', {
        method: H3.Http.MethodType.POST,
        data: params,
    });
}

/** 获取ai识别模板使用状态 */
export function getAIRecognitionTemplateUsedStatus(params: { templateCode: string }) {
    return request<H3.Http.Response.H3Response<boolean>>(
        `/v1/toolkit/ai/recognition/template/used/${params.templateCode}`,
        {
            method: H3.Http.MethodType.GET,
        },
    );
}

/** 获取ai识别模板使用状态 */
export function getAIRemainingCredit() {
    return request<H3.Http.Response.H3ResponseV2Beta<number>>('/v1/plugin/getAIRemainingQuantity', {
        method: H3.Http.MethodType.POST,
    });
}

/** 获取企业当天可用的ai生成应用次数 */
export function getAIGenerationAppUsedCount(functionCode: H3.AI.IAIGenerationCommon.getUsageType) {
    return request<H3.Http.Response.H3Response<H3.AI.Response.IGetAIRemainingCredit>>(
        `/v1/ai-agent/designer/function-usage?functionCode=${functionCode}`,
        {
            method: H3.Http.MethodType.GET,
        },
    );
}

/** 获取ai生成需求规格说明 */
export function getAIGenerationSpecification(businessDescription: string, forceGenerate = false) {
    return request<H3.Http.Response.H3Response<H3.AI.Response.IGetAIGenerationSpecification>>(
        '/v1/ai-agent/designer/app-generation/requirement-spec',
        {
            method: H3.Http.MethodType.POST,
            data: {
                businessDescription,
                forceGenerate,
            },
        },
    );
}

/** ai确认生成应用 */
export function confirmAIGenerationApp(businessDescription: string, needSampleData = true) {
    return request<H3.Http.Response.H3Response<H3.AI.Response.IStartAIGeneration>>(
        '/v1/ai-agent/designer/app-generation/task',
        {
            method: H3.Http.MethodType.POST,
            data: {
                businessDescription,
                needSampleData,
            },
            disableBizError: true,
        },
    );
}

/** 获取ai生成应用进度信息 */
export function getAIGenerationProgress(actionId: string) {
    return request<H3.Http.Response.H3Response<H3.AI.Response.IGetAIGenerationProgress>>(
        `/v1/ai-agent/designer/app-generation/task/${actionId}`,
        {
            method: H3.Http.MethodType.GET,
        },
    );
}

/** 获取ai生成表单信息 */
export function getAIRecommendControl(params: H3.AI.Request.IGetAIGenerationForm) {
    return request<H3.Http.Response.H3Response<H3.AI.Response.IGetAIGenerationForm>>(
        '/v1/ai-agent/designer/form-generation/field-inference',
        {
            method: H3.Http.MethodType.POST,
            data: params,
            disableBizError: true,
        },
    );
}

/** AI总结/分类 */
export function generateSummary(params: H3.AI.Request.IAIGenerationSummary) {
    return request<H3.Http.Response.H3Response<{ result: string }>>('/v1/ai-agent/skill/bizobject/extraction', {
        method: H3.Http.MethodType.POST,
        data: params,
        disableBizError: true,
    });
}

/** 知识库列表 */
export function getAIKnowledgeList(params: { offset: number; limit: number }) {
    return request<
        H3.Http.Response.H3Response<{
            enableList: H3.AI.IKnowledgeModel[];
            totalCount: number;
        }>
    >('/v1/toolkit/ai/feature/getAIKnowledgeList', {
        method: H3.Http.MethodType.POST,
        data: params,
    });
}

/** 获取知识库 */
export function getAIKnowledgeDetail(params: { code: string }) {
    return request<H3.Http.Response.H3Response<H3.AI.IKnowledgeModel>>(
        `/v1/toolkit/ai/recognition/template/${params.code}/detail`,
        {
            method: H3.Http.MethodType.GET,
        },
    );
}

/**
 * 连接AI助手
 * @param prompt 用户输入
 * @param sessionId 会话ID，不传表示新增会话
 * @param init request init
 */
export function connectAIAssistant(
    prompt: { appCode?: string; text: string },
    sessionId?: string,
    init: RequestInit = {},
) {
    return streamRequest('/v1/ai-agent/conversation/stream-message', {
        method: H3.Http.MethodType.POST,
        body: JSON.stringify({ ...prompt, conversationId: sessionId }),
        ...init,
    });
}

/**
 * 获取AI助手初始推荐问题
 * @param appCode 应用编码
 */
export function loadAIAssistantSuggestions(appCode?: string) {
    return request<H3.Http.Response.H3Response<string[]>>('/v1/ai-agent/conversation/init-tips', {
        method: H3.Http.MethodType.GET,
        params: { appCode },
    });
}

/**
 * 中断AI助手会话
 * @param conversationId 会话id
 */
export function interruptAIAssistant(conversationId: string, init: RequestInit = {}) {
    return request<H3.Http.Response.H3Response<boolean>>(`/v1/ai-agent/conversation/${conversationId}/interrupt`, {
        method: H3.Http.MethodType.POST,
        ...init,
    });
}

/** 保存知识库 */
export function saveAIKnowledge(params: H3.AI.IKnowledgeModel) {
    return request<H3.Http.Response.H3Response<H3.AI.IKnowledgeModel>>('/v1/toolkit/ai/recognition/template', {
        method: H3.Http.MethodType.POST,
        data: params,
    });
}

/** 删除知识库 */
export function deleteAIKnowledge(params: { code: string }) {
    return request<H3.Http.Response.H3Response<boolean>>(`/v1/toolkit/ai/recognition/template/${params.code}`, {
        method: H3.Http.MethodType.DELETE,
    });
}

/**
 * 获取历史会话
 * @param offset 偏移量
 * @param limit 分页大小
 */
export function getAIAssistantSessions(offset: number, limit: number) {
    return request<H3.Http.Response.H3Response<H3.AI.AIAssistant.Response.IGetSessions[]>>(
        '/v1/ai-agent/conversation',
        {
            method: H3.Http.MethodType.GET,
            params: {
                offset,
                limit,
            },
        },
    );
}

/**
 * 获取指定会话下消息
 * @param sessionId 会话id
 * @param offset 偏移量
 * @param limit 分页大小
 */
export function getAIAssistantMessages(sessionId: string, offset: number, limit: number, init: RequestInit = {}) {
    return request<H3.Http.Response.H3Response<H3.AI.AIAssistant.Response.IGetMessages[]>>(
        `/v1/ai-agent/conversation/${sessionId}/message`,
        {
            method: H3.Http.MethodType.GET,
            params: {
                offset,
                limit,
            },
            ...init,
        },
    );
}

/**
 * 更新指定会话标题
 * @param sessionId 会话id
 * @param title 标题
 */
export function updateAIAssistantSessionTitle(sessionId: string, title: string) {
    return request<H3.Http.Response.H3Response<undefined>>(`/v1/ai-agent/conversation/${sessionId}`, {
        method: H3.Http.MethodType.PATCH,
        data: {
            title,
        },
    });
}

export function deleteAIAssistantSession(sessionId: string) {
    return request<H3.Http.Response.H3Response<undefined>>(`/v1/ai-agent/conversation/${sessionId}`, {
        method: H3.Http.MethodType.DELETE,
    });
}

/**
 * 加载表单AI总结配置
 * @param schemaCode 表单编码
 */
export function loadSummarizationSetting(schemaCode: string) {
    return request<H3.Http.Response.H3Response<H3.AI.FormSummary.IAIFormSummarySetting>>(
        `/v1/toolkit/ai/summarization/setting/${schemaCode}`,
        {
            method: H3.Http.MethodType.GET,
        },
    );
}

/**
 * ai表单总结
 * @param schemaCode 表单编码
 * @param bizObjectId boId
 * @param bizObject bo
 * @param bizObjectChecksum boChecksum
 * @param setting 配置
 */
export function aiFormSummarization(
    schemaCode: string,
    bizObjectId: string,
    bizObject: Record<string, unknown>,
    bizObjectChecksum: string,
    setting: Pick<H3.AI.FormSummary.IAIFormSummarySetting, 'userConstraints'>,
) {
    return streamRequest('/v1/ai-agent/skill/bizobject/summarization', {
        method: H3.Http.MethodType.POST,
        body: JSON.stringify({
            schemaCode,
            bizObjectId,
            bizObject,
            bizObjectChecksum,
            userConstraints: setting.userConstraints,
        }),
    });
}
